import { Analysis, PossibleRootCauseCategory, ProbabilityCategory } from '.';
import {
  deviationRank,
  emptyTally,
  hybridProbability,
  isPastMidpoint,
  probabilityCategory,
  tallyMarker,
} from './gaugeSeverity';
import { getIronPatternInsight } from './iron-pattern-insights';

/**
 * Iron Status & Utilization.
 *
 * Two layers live here:
 *  1. The class `calculateInsights()` pushes the per-marker 3xxx Possible Root
 *     Cause categories (Causes tab content in DynamoDB), unchanged.
 *  2. The pure, exported `resolveIronPattern` + `ironGauge` drive the Iron
 *     Insights panel (iron-status-insights.tsx): one pattern from the iron
 *     quartet (Iron, TIBC, % Saturation, Ferritin), and a 0-100 likelihood
 *     over the markers that pattern names.
 *
 * The patterns are NOT new clinical content. They are the iron-quartet part of
 * the "Root Causes w Iron" rules that already fire inside the RBC section
 * (BloodworkAnalysis.calculatePossibleRootCausesWithIronPanel, categoryIds
 * 300-420), with the RBC gates (anemia likelihood, red-cell size and color
 * categories 2-5, the Platelet/MCH B12 split) left off, because those belong
 * to the RBC panel. What is left is the iron picture on its own, which is what
 * this section is for. The 97xxx ids are a new block (Glucose 72xxx, Thyroid
 * 80xxx, Digestion 85xxx, Nutrients 87xxx, Adrenal 90xxx, Liver 95xxx planned)
 * and have NO DynamoDB rows yet, so the class does not push them: like
 * Thyroid's 80xxx, they exist for the panel and the raw-data export only.
 *
 * Sheet Logic wording → states: "< Opt" is `low`, "= Opt" is `opt`, "> Opt"
 * is `high`, "BLANK" is `absent`; "<= Opt" is low or opt (measured), and
 * "BLANK OR <= Opt" is that or absent.
 */

/** Per-marker state. 'absent' = not measured (or no range to read it by). */
export type IronTri = 'low' | 'opt' | 'high' | 'absent';

export interface IronMarkerStates {
  iron: IronTri;
  tibc: IronTri;
  saturation: IronTri;
  ferritin: IronTri;
  /** Absolute neutrophils below optimal — the rules' copper-insufficiency refinement (2.16-2.21, 2.26, 2.34, 2.36). */
  neutrophilsLow: boolean;
  /** WBC, for the infection refinements of the serum-iron pattern (2.30 / 2.31, RBC categories 71 / 72). */
  wbc: IronTri;
  /** Immune System Abnormality reads Possible or higher while WBC is optimal (RBC category 73 → rule 2.38). */
  immuneAbnormalityPossible: boolean;
}

/** The iron pattern categoryIds, by name (see iron-pattern-insights.ts for the content). */
export const IRON_PATTERN = {
  INSUFFICIENCY: 97100,
  STORAGE_INSUFFICIENCY: 97110,
  STORAGE_INSUFFICIENCY_UNDETERMINED: 97120,
  INSUFFICIENCY_COPPER: 97130,
  STORAGE_INSUFFICIENCY_COPPER: 97140,
  STORAGE_INSUFFICIENCY_UNDETERMINED_COPPER: 97150,
  SERUM_INSUFFICIENCY_INFLAMMATION: 97200,
  SERUM_INSUFFICIENCY_CHRONIC_INFECTION: 97210,
  SERUM_INSUFFICIENCY_ACUTE_INFECTION: 97220,
  SERUM_INSUFFICIENCY_IMMUNE_ABNORMALITY: 97230,
  STORAGE_EXCESS_INFLAMMATION: 97300,
  SERUM_EXCESS: 97310,
} as const;

/**
 * Resolve the marker states to ONE iron pattern categoryId (97xxx), or null
 * when nothing fires. The three groups are mutually exclusive on ferritin and
 * % saturation, so order only matters inside a group (first match wins).
 *
 * Two deliberate generalizations over the RBC rules, both because the panel
 * reads the iron quartet on its own and a client is often missing one of them:
 *   • Iron and TIBC may be absent in the low-ferritin and low-saturation
 *     groups (the rules required them measured). % saturation is derived from
 *     the two, so when it is present they were both run; when it is absent the
 *     undetermined variant (1.3) says so.
 *   • Group 3's inflammatory-ferritin pattern needs ferritin ABOVE optimal.
 *     Rules 2.1 / 2.28 accept an optimal ferritin because, inside the RBC
 *     section, anemia that iron does not explain is itself the finding; here
 *     there is no anemia gate, and an all-optimal quartet must read balanced.
 */
export function resolveIronPattern(s: IronMarkerStates): number | null {
  const { iron, tibc, saturation: sat, ferritin } = s;
  const absent = (x: IronTri) => x === 'absent';
  const low = (x: IronTri) => x === 'low';
  const opt = (x: IronTri) => x === 'opt';
  const high = (x: IronTri) => x === 'high';
  // Sheet "<= Opt" / ">= Opt" (measured), and their "BLANK OR" forms.
  const notHigh = (x: IronTri) => low(x) || opt(x);
  const notLow = (x: IronTri) => high(x) || opt(x);
  const absentOrNotHigh = (x: IronTri) => absent(x) || notHigh(x);
  const absentOrNotLow = (x: IronTri) => absent(x) || notLow(x);

  if (absent(iron) && absent(tibc) && absent(sat) && absent(ferritin)) return null;

  // ── Group 1: ferritin below optimal ──────────────────────────────────────
  // Iron <= Opt; TIBC >= Opt; Ferritin < Opt; % Saturation splits the two
  // findings (< Opt → Iron Insufficiency, = Opt → Iron Storage Insufficiency);
  // both absent → storage insufficiency with serum iron undetermined (2.14).
  // Absolute neutrophils < Opt turns each into its copper-insufficiency twin.
  if (low(ferritin) && absentOrNotHigh(iron)) {
    const copper = s.neutrophilsLow;
    if (absent(sat)) {
      return copper
        ? IRON_PATTERN.STORAGE_INSUFFICIENCY_UNDETERMINED_COPPER
        : IRON_PATTERN.STORAGE_INSUFFICIENCY_UNDETERMINED;
    }
    if (absentOrNotLow(tibc)) {
      if (low(sat)) return copper ? IRON_PATTERN.INSUFFICIENCY_COPPER : IRON_PATTERN.INSUFFICIENCY;
      if (opt(sat)) return copper ? IRON_PATTERN.STORAGE_INSUFFICIENCY_COPPER : IRON_PATTERN.STORAGE_INSUFFICIENCY;
    }
    // Low ferritin with a HIGH saturation or a LOW TIBC is not one of the
    // authored pictures; the per-marker Causes rows carry it.
    return null;
  }

  // ── Group 2: % saturation below optimal, ferritin held (>= Opt) ──────────
  // Iron BLANK OR <= Opt; TIBC BLANK OR <= Opt; % Saturation < Opt;
  // Ferritin >= Opt (2.29), refined by the WBC picture (2.30 / 2.31 / 2.38).
  if (low(sat) && notLow(ferritin) && absentOrNotHigh(iron) && absentOrNotHigh(tibc)) {
    if (low(s.wbc)) return IRON_PATTERN.SERUM_INSUFFICIENCY_CHRONIC_INFECTION;
    if (high(s.wbc)) return IRON_PATTERN.SERUM_INSUFFICIENCY_ACUTE_INFECTION;
    if (opt(s.wbc) && s.immuneAbnormalityPossible) return IRON_PATTERN.SERUM_INSUFFICIENCY_IMMUNE_ABNORMALITY;
    return IRON_PATTERN.SERUM_INSUFFICIENCY_INFLAMMATION;
  }

  // ── Group 3: iron loading, or ferritin up without it ─────────────────────
  // 2.8: Iron >= Opt; TIBC <= Opt; % Saturation > Opt; Ferritin >= Opt.
  if (high(sat) && notLow(ferritin) && absentOrNotLow(iron) && absentOrNotHigh(tibc)) {
    return IRON_PATTERN.SERUM_EXCESS;
  }
  // 2.1 / 2.28: Iron BLANK OR <= Opt; TIBC BLANK OR <= Opt; Ferritin above
  // optimal with % saturation optimal or not run (a low saturation is group 2,
  // a high one is 2.8 above).
  if (high(ferritin) && absentOrNotHigh(iron) && absentOrNotHigh(tibc) && (absent(sat) || opt(sat))) {
    return IRON_PATTERN.STORAGE_EXCESS_INFLAMMATION;
  }

  return null;
}

/**
 * Gauge condition — the noun the needle reads against ("{label} is Likely").
 * Named for the resolved pattern's family so the gauge and the box beneath it
 * agree; 'imbalance' is the neutral reading when markers are off without an
 * authored pattern (or for the inflammatory-ferritin pattern, which is not an
 * iron condition as such), and 'balanced' when nothing deviates.
 */
export type IronCondition =
  | 'insufficiency'
  | 'storage'
  | 'serum'
  | 'excess'
  | 'imbalance'
  | 'balanced';

/** A direction arrow in a pattern box's marker row. */
export interface IronMarkerArrow {
  direction: 'up' | 'down';
  label: string;
}

export interface IronGauge {
  /** 0–100 needle position for the standard sweep gauge (LabsmartsGuage). */
  probability: number;
  category: ProbabilityCategory;
  condition: IronCondition;
  /** The resolved 97xxx pattern, or null when none fires. */
  patternId: number | null;
  /** The markers the needle was scored from, in panel order. */
  markers: IronMarkerArrow[];
}

/** The marker-like objects the gauge reads: PanelMarkers in the app, plain
 *  `{ value, range, hasValue }` objects in tests. */
export interface IronGaugeInput {
  iron?: any;
  tibc?: any;
  saturation?: any;
  ferritin?: any;
  neutrophilsAbs?: any;
  wbc?: any;
}

const usable = (m?: any) => !!m?.hasValue?.() && !!m.range;

/** Tri-state of one marker from its raw value and range (absent when either is missing). */
export function ironTriState(marker?: any): IronTri {
  if (!usable(marker)) return 'absent';
  if (deviationRank(marker, 'low') != null) return 'low';
  if (deviationRank(marker, 'high') != null) return 'high';
  return 'opt';
}

/** The resolver's input, built from the markers the gauge is given. */
export function ironMarkerStates(
  input: IronGaugeInput,
  immuneAbnormalityPossible: boolean = false,
): IronMarkerStates {
  return {
    iron: ironTriState(input.iron),
    tibc: ironTriState(input.tibc),
    saturation: ironTriState(input.saturation),
    ferritin: ironTriState(input.ferritin),
    neutrophilsLow: ironTriState(input.neutrophilsAbs) === 'low',
    wbc: ironTriState(input.wbc),
    immuneAbnormalityPossible,
  };
}

const CONDITION_BY_PATTERN: Record<number, IronCondition> = {
  [IRON_PATTERN.INSUFFICIENCY]: 'insufficiency',
  [IRON_PATTERN.INSUFFICIENCY_COPPER]: 'insufficiency',
  [IRON_PATTERN.STORAGE_INSUFFICIENCY]: 'storage',
  [IRON_PATTERN.STORAGE_INSUFFICIENCY_UNDETERMINED]: 'storage',
  [IRON_PATTERN.STORAGE_INSUFFICIENCY_COPPER]: 'storage',
  [IRON_PATTERN.STORAGE_INSUFFICIENCY_UNDETERMINED_COPPER]: 'storage',
  [IRON_PATTERN.SERUM_INSUFFICIENCY_INFLAMMATION]: 'serum',
  [IRON_PATTERN.SERUM_INSUFFICIENCY_CHRONIC_INFECTION]: 'serum',
  [IRON_PATTERN.SERUM_INSUFFICIENCY_ACUTE_INFECTION]: 'serum',
  [IRON_PATTERN.SERUM_INSUFFICIENCY_IMMUNE_ABNORMALITY]: 'serum',
  [IRON_PATTERN.STORAGE_EXCESS_INFLAMMATION]: 'imbalance',
  [IRON_PATTERN.SERUM_EXCESS]: 'excess',
};

// The quartet in section order, which is also the arrow row's order.
const QUARTET: Array<{ key: keyof IronGaugeInput; label: string }> = [
  { key: 'iron', label: 'Iron' },
  { key: 'tibc', label: 'TIBC' },
  { key: 'saturation', label: '% Sat' },
  { key: 'ferritin', label: 'Ferritin' },
];

/** The quartet keys that read serum iron TRANSPORT, scored as one slot (see
 *  `scoreQuartet`). Ferritin, the storage marker, is the other slot. */
const TRANSPORT_KEYS: Array<keyof IronGaugeInput> = ['iron', 'tibc', 'saturation'];

/**
 * How one marker reads in one direction, on a scale that orders two within-lab
 * deviations: rank (1 within lab / 2 past lab / 3 past alarm) doubled, plus one
 * for a value past the lab↔optimal midpoint. The same tie-break the Adrenal
 * gauge uses to collapse Cortisol AM/PM, and for the same reason: without it,
 * measuring a second, milder marker can win the comparison and grade the slot
 * DOWN. -1 when the marker is optimal, unmeasured, or not deviating that way.
 */
function markerSeverity(marker: any, dir: 'low' | 'high'): number {
  const r = deviationRank(marker, dir);
  return r == null ? -1 : r * 2 + (isPastMidpoint(marker, dir) ? 1 : 0);
}

/** One quartet marker's reading, as both the gauge and the arrow row see it. */
interface IronScoredMarker {
  key: keyof IronGaugeInput;
  label: string;
  direction: 'low' | 'high';
  /** deviationRank in `direction`. */
  rank: number;
  /** isPastMidpoint in `direction`. */
  pastMid: boolean;
}

/**
 * Every quartet marker this client has off, in panel order, each with the
 * direction it is read in. ONE resolution shared by the needle (`ironGauge`)
 * and the box's ▲/▼ row (`ironPatternArrows`), so the panel can never show a
 * marker the score ignored, or the reverse.
 *
 * A marker is read in the direction the resolved pattern's signature names
 * (any direction when no pattern fired), PLUS any direction it is outside the
 * lab range in. The second half is why a pattern cannot hide a marker: the
 * signatures name only the markers that corroborate their finding, so a TIBC
 * past its alarm bound used to vanish from a low-ferritin panel, taking the
 * needle down with it (a mildly low ferritin beside a past-alarm TIBC read
 * 55%, where the same TIBC with an OPTIMAL ferritin read 95%). Out of lab is
 * out of lab, whichever finding it points at.
 */
function scoreQuartet(
  input: IronGaugeInput,
  signature?: Array<{ key: string; dirs: Array<'low' | 'high'> }>,
): IronScoredMarker[] {
  const out: IronScoredMarker[] = [];
  for (const { key, label } of QUARTET) {
    const m = (input as any)[key];
    const sig = signature ? signature.find(l => l.key === key) : undefined;
    const dirs: Array<'low' | 'high'> = signature ? (sig ? [...sig.dirs] : []) : ['low', 'high'];
    for (const d of ['low', 'high'] as const) {
      if (dirs.indexOf(d) < 0 && (deviationRank(m, d) || 0) >= 2) dirs.push(d);
    }
    let best: IronScoredMarker | null = null;
    for (const d of dirs) {
      const rank = deviationRank(m, d);
      if (rank == null) continue;
      if (!best || markerSeverity(m, d) > markerSeverity(m, best.direction)) {
        best = { key, label, direction: d, rank, pastMid: isPastMidpoint(m, d) };
      }
    }
    if (best) out.push(best);
  }
  return out;
}

const toArrow = (m: IronScoredMarker): IronMarkerArrow => ({
  direction: m.direction === 'low' ? 'down' : 'up',
  label: m.label,
});

/**
 * The ▲/▼ row for a resolved pattern: the quartet markers the gauge scored
 * (panel order, `scoreQuartet`), then the signature's non-quartet markers (WBC
 * and absolute neutrophils, the refinements that name the pattern without
 * moving the needle) in signature order. A marker that reads optimal, or was
 * not run, draws nothing.
 */
export function ironPatternArrows(patternId: number, input: IronGaugeInput): IronMarkerArrow[] {
  const insight = getIronPatternInsight(patternId);
  if (!insight) return [];
  const out: IronMarkerArrow[] = scoreQuartet(input, insight.logic).map(toArrow);
  for (const { label, key, dirs } of insight.logic) {
    if (QUARTET.some(q => q.key === key)) continue;
    const m = (input as any)[key];
    if (dirs.indexOf('high') >= 0 && deviationRank(m, 'high') != null) out.push({ direction: 'up', label });
    else if (dirs.indexOf('low') >= 0 && deviationRank(m, 'low') != null) out.push({ direction: 'down', label });
  }
  return out;
}

/**
 * Map the iron quartet to the standard-sweep gauge state. Pure and null-safe
 * so it can be unit-tested with plain objects and reused by the panel. Null
 * when none of the four is usable (unmeasured or range-less).
 *
 * Direction comes from the resolved pattern (its family names the gauge);
 * severity is the same Glucose-style hybrid the Adrenal gauge uses
 * (gaugeSeverity.hybridProbability). Which markers are read, and in which
 * direction, is `scoreQuartet`.
 *
 * THE TALLY IS TWO SLOTS, NOT FOUR MARKERS, because the quartet is not four
 * measurements: **% saturation is DERIVED** from iron and TIBC
 * (BloodworkDerivation, `(iron / TIBC) × 100`), and the entry form fills it
 * whenever both were run. Counting it as its own marker scored one analyte
 * twice: an adult woman with iron 35 (below a lab floor of 40) beside an
 * optimal TIBC and an optimal ferritin derived a % saturation of 11.7, and two
 * "out of lab" markers stacked to 97% off one abnormal result. So:
 *   • **transport** — iron, TIBC and % saturation, the WORST of the three by
 *     `markerSeverity` (the Cortisol AM/PM collapse), counted once;
 *   • **storage** — ferritin, counted once.
 * The ceiling is therefore 97, not 99: two slots cannot put three markers past
 * the lab range. Both are still Highly Likely, and the 99 rung stays in
 * gaugeSeverity for the gauges whose markers are genuinely independent.
 *
 * Nothing deviating → balanced, Less Likely, with the needle scaled by how much
 * evidence was gathered (Glucose's 5 / 15 / 25 confidence tiers): ferritin and
 * % saturation read the picture most directly, so both present reads most
 * confident, one of them is the middle rung, iron and/or TIBC alone stays at 25.
 */
export function ironGauge(
  input: IronGaugeInput,
  immuneAbnormalityPossible: boolean = false,
): IronGauge | null {
  const { iron, tibc, saturation, ferritin } = input;
  if (!usable(iron) && !usable(tibc) && !usable(saturation) && !usable(ferritin)) {
    return null;
  }

  const states = ironMarkerStates(input, immuneAbnormalityPossible);
  const patternId = resolveIronPattern(states);
  const signature = patternId != null ? getIronPatternInsight(patternId)?.logic : undefined;

  // Every marker that is off, for the panel to show; the tally below reads the
  // same set as two slots.
  const scored = scoreQuartet(input, signature);
  const markers: IronMarkerArrow[] = scored.map(toArrow);

  const tally = emptyTally();
  const transport = scored
    .filter(m => TRANSPORT_KEYS.indexOf(m.key) >= 0)
    .reduce(
      (worst: IronScoredMarker | null, m) =>
        !worst ||
        markerSeverity((input as any)[m.key], m.direction) >
          markerSeverity((input as any)[worst.key], worst.direction)
          ? m
          : worst,
      null,
    );
  const storage = scored.find(m => m.key === 'ferritin');
  if (transport) tallyMarker(tally, transport.rank, transport.pastMid);
  if (storage) tallyMarker(tally, storage.rank, storage.pastMid);

  if (markers.length === 0) {
    const probability =
      usable(ferritin) && usable(saturation) ? 5 : usable(ferritin) || usable(saturation) ? 15 : 25;
    return {
      probability,
      category: probabilityCategory(probability),
      condition: 'balanced',
      patternId: null,
      markers: [],
    };
  }

  const probability = hybridProbability(tally);
  return {
    probability,
    category: probabilityCategory(probability),
    condition: patternId != null ? CONDITION_BY_PATTERN[patternId] : 'imbalance',
    patternId,
    markers,
  };
}

export class IronAnalysis extends Analysis {
    calculateInsights() {
        if (this.ba.iron.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_3000);
        } else if (this.ba.iron.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_3010);
        }
        
        if (this.ba.iron.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_3015);
        } else if (this.ba.saturation.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_3015);
        }

        if (this.ba.tibc.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_3020);
        } else if (this.ba.tibc.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_3030);
        }

        if (this.ba.saturation.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_3040);
        } else if (this.ba.saturation.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_3050);
        }

        if (this.ba.ferritin.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_3060);
        } else if (this.ba.ferritin.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_3070);
        }

        if (this.ba.transferrin.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_3080);
        } else if (this.ba.transferrin.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_3090);
        }

        if (this.ba.ceruloplasmin.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_3100);
        } else if (this.ba.ceruloplasmin.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_3110);
        }

        if (this.ba.copperRBCμgdL.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_3120);
        } else if (this.ba.copperRBCμgdL.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_3130);
        }

        if (this.ba.copperRBCμgmL.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_3120);
        } else if (this.ba.copperRBCμgmL.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_3130);
        }

        if (this.ba.copperSerummcgmL.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_3140);
        } else if (this.ba.copperSerummcgmL.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_3150);
        }

        if (this.ba.copperSerumμgdL.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_3140);
        } else if (this.ba.copperSerumμgdL.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_3150);
        }

        if (this.ba.percentOfFreeCopper.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_3160);
        } else if (this.ba.percentOfFreeCopper.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_3170);
        }

        if (this.ba.vitaminA.isGreaterThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_3172);
        } else if (this.ba.vitaminA.isStricklyLessThanOptimal()) {
            this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_3174);
        }
    }
}